import { useRef, useCallback, useEffect } from 'react';
import { formatNumber } from '../services/NumberService';

const TOOLTIP_VALUE_FORMATTERS = {
  currency: (value) => `$${formatNumber(value, 2)}`,
  percentage: (value) => `${formatNumber(value)}%`,
};

const TOOLTIP_VALUE_PATTERN = /(:\s*)(-?[\d,]+\.?\d*)(\s*)$/;

export const formatTooltipValues = (root, format) => {
  const formatter = TOOLTIP_VALUE_FORMATTERS[format];
  if (!formatter) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const match = textNode.nodeValue.match(TOOLTIP_VALUE_PATTERN);
    if (!match) return;
    const [prefix, rawValue, suffix] = match;
    const numericValue = Number(rawValue.replace(/,/g, ''));
    if (Number.isNaN(numericValue)) return;
    textNode.nodeValue = textNode.nodeValue.replace(
      TOOLTIP_VALUE_PATTERN,
      () => `${prefix}${formatter(numericValue)}${suffix}`
    );
  });
};

const useGoogleChartTooltip = (position = 'bottom', format) => {
  const lastMouse = useRef({ x: 0, y: 0 });
  const chartContainerRef = useRef(null);
  const tooltipObserverRef = useRef(null);

  const positionTooltip = useCallback(() => {
    if (!chartContainerRef.current) return;
    const tooltip = chartContainerRef.current.querySelector(
      '.google-visualization-tooltip'
    );
    if (!tooltip || tooltip.style.visibility === 'hidden') return;
    formatTooltipValues(tooltip, format);
    tooltip.style.position = 'fixed';
    tooltip.style.left =
      position === 'top'
        ? lastMouse.current.x - tooltip.offsetWidth / 2 + 'px'
        : lastMouse.current.x - tooltip.offsetWidth - 15 + 'px';
    tooltip.style.top =
      position === 'top'
        ? lastMouse.current.y - tooltip.offsetHeight - 15 + 'px'
        : lastMouse.current.y + 15 + 'px';
  }, [position, format]);

  const followTooltip = useCallback(
    (e) => {
      lastMouse.current = { x: e.clientX, y: e.clientY };
      positionTooltip();
    },
    [positionTooltip]
  );

  const setChartContainerRef = useCallback(
    (element) => {
      chartContainerRef.current = element;
      if (tooltipObserverRef.current) {
        tooltipObserverRef.current.disconnect();
        tooltipObserverRef.current = null;
      }
      if (element) {
        tooltipObserverRef.current = new MutationObserver(() =>
          positionTooltip()
        );
        tooltipObserverRef.current.observe(element, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style'],
        });
      }
    },
    [positionTooltip]
  );

  useEffect(() => {
    return () => {
      if (tooltipObserverRef.current) {
        tooltipObserverRef.current.disconnect();
      }
    };
  }, []);

  return { setChartContainerRef, followTooltip };
};

export default useGoogleChartTooltip;
